import React from 'react';
import { SecondaryNavGroup } from './MegaMenu';
import { LinkColumn } from './LinkColumn';
import { FeaturedColumn } from './FeaturedColumn';
import { NavFooter } from './NavFooter';
import './DropdownPanel.scss';
import './LinkColumn.scss';

/**
 * DropdownPanel - Container that renders below the nav bar when an L1 item is selected
 *
 * Displays secondary navigation content (LinkColumns, FeaturedColumn, NavFooter)
 * within a max-width container. Includes a backdrop overlay that closes the panel on click.
 *
 * Features:
 * - Renders below the primary navigation bar
 * - Backdrop overlay with click-to-close
 * - Max-width container matching nav bar width
 * - Receives link columns, featured data, and footer items as props
 * - Self-contained: no knowledge of sibling components
 */

export interface DropdownPanelProps {
  /** Whether the panel is currently open/visible */
  isOpen: boolean;
  /** Array of link column data to render within the panel */
  linkColumns: SecondaryNavGroup[];
  /** Featured column image URL */
  featuredImageUrl?: string;
  /** Featured column link URL */
  featuredUrl?: string;
  /** Featured column CTA ID */
  featuredCtaId?: string;
  /** Featured column title */
  featuredTitle?: string;
  /** Featured column description */
  featuredDescription?: string;
  /** Featured column badge text */
  featuredBadgeText?: string;
  /** Featured column badge icon URL */
  featuredBadgeIconUrl?: string;
  /** Video URL — when set, featured image opens a video lightbox modal */
  featuredVideoUrl?: string;
  /** Featured column size variant */
  featuredSize?: 'sm' | 'lg';
  /** Footer items to render at the bottom of the panel */
  footerItems?: Array<{ id: string; title: string; url: string; target?: '_blank' | '_self' }>;
  /** Callback when the panel should close (backdrop click) */
  onClose: () => void;
  /** Panel ID for aria-controls association with PrimaryNavItem */
  panelId?: string;
}

export const DropdownPanel: React.FC<DropdownPanelProps> = ({
  isOpen,
  linkColumns,
  featuredImageUrl,
  featuredUrl,
  featuredCtaId,
  featuredTitle,
  featuredDescription,
  featuredBadgeText,
  featuredBadgeIconUrl,
  featuredVideoUrl,
  featuredSize,
  footerItems,
  onClose,
  panelId,
}) => {
  if (!isOpen) {
    return null;
  }

  const hasFeatured = !!(featuredImageUrl && featuredTitle && (featuredUrl || featuredVideoUrl));

  return (
    <div
      className="poly-dropdown-panel"
      id={panelId}
      role="region"
    >
      <div className="poly-dropdown-panel__content">
        {linkColumns.map((column, index) => (
          <LinkColumn
            key={`${column.title}-${index}`}
            title={column.title}
            links={column.links}
            columnType={column.columnType}
            actionText={column.actionText}
            actionUrl={column.actionUrl}
            actionCtaId={column.actionCtaId}
            variant={column.variant}
          />
        ))}
        {hasFeatured && (
          <FeaturedColumn
            imageUrl={featuredImageUrl!}
            url={featuredUrl!}
            ctaId={featuredCtaId}
            title={featuredTitle!}
            description={featuredDescription}
            badgeText={featuredBadgeText}
            badgeIconUrl={featuredBadgeIconUrl}
            videoUrl={featuredVideoUrl}
            size={featuredSize}
          />
        )}
      </div>
      {footerItems && footerItems.length > 0 && (
        <NavFooter items={footerItems} />
      )}
      {/* Backdrop overlay — positioned below the panel via top: 100% */}
      <div
        className="poly-dropdown-panel__backdrop"
        onClick={onClose}
        data-testid="dropdown-panel-backdrop"
        aria-hidden="true"
      />
    </div>
  );
};
